import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Divider,
  CircularProgress,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FaDownload } from 'react-icons/fa';
import { useProcess } from '../context/ProcessContext';
import { useActivityLog } from '../context/ActivityLogContext';
import InterfaceHeader from '../components/InterfaceHeader';
import FavoriteButton from '../components/FavoriteButton';
import { API_ENDPOINTS, apiCall } from '../config/api';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const MarketingCopies = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const primaryColor = TOOL_COLORS.OTHER.primary;
  const primaryHover = TOOL_COLORS.OTHER.hover;
  const { processes, addProcess, updateProcess, removeProcess } = useProcess();
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs: setActivityLogs,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();
  const fileInputRef = useRef(null);
  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  const [pipelineTab, setPipelineTab] = useState(0); // 0 = Classic, 1 = DTC Framework
  const pipelineMode = pipelineTab === 0 ? 'classic' : 'dtc';

  const [file, setFile] = useState(null);
  const [useExisting, setUseExisting] = useState(false);
  const [isRugs, setIsRugs] = useState(true);
  const [characterLimit, setCharacterLimit] = useState('1000');
  const [processing, setProcessing] = useState(false);
  const [processId, setProcessId] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      color: theme.palette.text.primary,
      '& fieldset': { borderColor: isDarkMode ? alpha('#fff', 0.23) : alpha('#000', 0.23) },
      '&:hover fieldset': { borderColor: primaryColor },
      '&.Mui-focused fieldset': { borderColor: primaryColor },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
  };

  // Poll for status
  useEffect(() => {
    const currentStatus = status?.status || status?.Status;
    const isComplete = currentStatus === 'completed' || currentStatus === 'error';
    if (processId && (processing || (status && !isComplete))) {
      intervalRef.current = setInterval(() => fetchStatus(), 1000);
      setIntervalRef(processId, intervalRef.current);
      return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }
  }, [processing, processId, status?.status, status?.Status]);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) validateAndSetFile(selectedFile);
  };

  const validateAndSetFile = (selectedFile) => {
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(ext)) { setError('Please select a valid Excel (.xlsx, .xls) or CSV file'); return; }
    if (selectedFile.size > 50 * 1024 * 1024) { setError('File size must be less than 50MB'); return; }
    setFile(selectedFile);
    setError(null);
  };

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) validateAndSetFile(droppedFile);
  };

  const startProcessing = async () => {
    if (!file) { setError('Please select a file first'); return; }
    downloadTriggeredRef.current = false;
    setStatus(null); setProcessId(null); processIdRef.current = null;
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('useExisting', useExisting);
    formData.append('isRugs', isRugs);
    formData.append('characterLimit', characterLimit);
    formData.append('pipelineMode', pipelineMode);

    try {
      setProcessing(true); setError(null); setSuccess(null);
      const data = await apiCall(API_ENDPOINTS.MARKETING_COPIES_PROCESS, { method: 'POST', body: formData });
      if (data.success) {
        setProcessId(data.processId);
        processIdRef.current = data.processId;
        const procName = pipelineTab === 0 ? 'Marketing Copy Generation' : 'DTC Copy Generation';
        addActivityProcess(data.processId, procName, 'Marketing Copies');
        addProcess({ id: data.processId, name: procName, progress: 0, status: 'processing' });
      } else {
        setError(data.message || 'Failed to start processing'); setProcessing(false);
      }
    } catch (err) { setError(`Error: ${err.message}`); setProcessing(false); }
  };

  const fetchStatus = async () => {
    const currentProcessId = processIdRef.current || processId;
    if (!currentProcessId) return;
    try {
      const data = await apiCall(API_ENDPOINTS.MARKETING_COPIES_STATUS(currentProcessId), { method: 'GET' });
      if (data.success) {
        const s = data.status;
        setStatus(s);
        const pct = s.progressPercentage ?? s.ProgressPercentage ?? 0;
        const total = s.totalItems ?? s.TotalItems ?? 0;
        const processed = s.processedItems ?? s.ProcessedItems ?? 0;
        const logs = s.logs ?? s.Logs ?? [];
        const st = s.status ?? s.Status ?? '';
        updateActivityProcess(currentProcessId, { progressPercentage: pct, totalItems: total, processedItems: processed });
        if (logs.length > 0) setActivityLogs(currentProcessId, logs);
        updateProcess(currentProcessId, { progress: pct, status: st });
        if (st === 'completed' || st === 'error') {
          setProcessing(false);
          if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
          clearIntervalRef(currentProcessId);
          completeActivityProcess(currentProcessId, st === 'completed');
          if (st === 'completed') {
            setSuccess('Marketing copy generation completed!');
            const awsUrl = s.awsUrl ?? s.AwsUrl ?? null;
            if (!downloadTriggeredRef.current) {
              downloadTriggeredRef.current = true;
              if (awsUrl) {
                // Download via S3 URL
                const link = document.createElement('a');
                link.href = awsUrl;
                link.download = `Marketing Copy Result.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                // Also open in new tab
                window.open(awsUrl, '_blank');
              } else {
                downloadResult(currentProcessId);
              }
            }
          } else { setError('Processing failed.'); }
        }
      }
    } catch (err) { console.error('Error fetching status:', err); }
  };

  const downloadResult = async (pId) => {
    const downloadProcessId = pId || processId;
    if (!downloadProcessId) return;
    try {
      setError(null);
      const response = await apiCall(`${API_ENDPOINTS.MARKETING_COPIES_DOWNLOAD(downloadProcessId)}?t=${Date.now()}`, {
        method: 'GET',
        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache', 'Expires': '0' },
      });
      const blob = await response.blob();
      if (blob.size === 0) throw new Error('Downloaded file is empty');
      const contentDisposition = response.headers.get('Content-Disposition');
      const nowResult = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
      let filename = `Marketing Copy Result ${nowResult.replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none'; a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      setTimeout(() => { if (document.body.contains(a)) document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch (err) { setError(`Download failed: ${err.message}`); }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(API_ENDPOINTS.MARKETING_COPIES_TEMPLATE, {
        method: 'GET',
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      });
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const now = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
      const filename = `Marketing Copy Template ${now.replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) { setError(`Template download failed: ${err.message}`); }
  };

  const clearFile = () => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; };
  const handleReset = () => {
    setFile(null); setUseExisting(false); setIsRugs(true); setCharacterLimit('1000');
    setProcessing(false); setProcessId(null); processIdRef.current = null;
    setStatus(null); setError(null); setSuccess(null);
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const statusVal = status?.status ?? status?.Status ?? '';
  const progressPct = status?.progressPercentage ?? status?.ProgressPercentage ?? 0;
  const totalItems = status?.totalItems ?? status?.TotalItems ?? 0;
  const processedItems = status?.processedItems ?? status?.ProcessedItems ?? 0;
  const failedItems = status?.failedItems ?? status?.FailedItems ?? 0;
  const logs = status?.logs ?? status?.Logs ?? [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', position: 'relative' }}>
      <FavoriteButton interfaceName="Marketing Copy Generator" category="other" />
      <InterfaceHeader
        title="Marketing Copy Generator"
        color={primaryColor}
        category="other"
      />

      {/* Pipeline Mode Tabs */}
      <Paper sx={{ mb: 3, border: `1px solid ${theme.palette.divider}` }}>
        <Tabs
          value={pipelineTab}
          onChange={(e, v) => { if (!processing) setPipelineTab(v); }}
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.95rem' },
            '& .MuiTab-root.Mui-selected': { color: primaryColor },
            '& .MuiTabs-indicator': { backgroundColor: primaryColor },
          }}
        >
          <Tab label="Classic" />
          <Tab label="DTC Framework" />
        </Tabs>
        <Box sx={{ px: 2, py: 1, bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
          <Typography variant="caption" color="text.secondary">
            {pipelineTab === 0
              ? 'Original generator — produces free-form marketing paragraphs using a general copywriting prompt.'
              : 'DTC Framework — produces structured copy (Title, Description, 5 Bullets, Care, Specs) following the Safavieh DTC Copy Framework with Craft/Design voice assignment.'}
          </Typography>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column — Upload & Config */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: primaryColor }}>
              Upload & Process
            </Typography>

            {/* Product Category Toggle */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Product Category</Typography>
              <RadioGroup value={isRugs ? 'rugs' : 'non-rugs'} onChange={(e) => setIsRugs(e.target.value === 'rugs')} row>
                <FormControlLabel value="rugs" control={<Radio sx={{ '&.Mui-checked': { color: primaryColor } }} />} label="Rugs" />
                <FormControlLabel value="non-rugs" control={<Radio sx={{ '&.Mui-checked': { color: primaryColor } }} />} label="Non-Rugs (Furniture, Lighting, etc.)" />
              </RadioGroup>
            </Box>

            {/* Marketing Copy Option */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Generation Mode</Typography>
              <RadioGroup value={useExisting ? 'existing' : 'new'} onChange={(e) => setUseExisting(e.target.value === 'existing')} row>
                <FormControlLabel value="new" control={<Radio sx={{ '&.Mui-checked': { color: primaryColor } }} />} label="Generate New (AI)" />
                <FormControlLabel value="existing" control={<Radio sx={{ '&.Mui-checked': { color: primaryColor } }} />} label="Use Existing Copy" />
              </RadioGroup>
            </Box>

            {/* Character Limit — Classic mode only */}
            {pipelineTab === 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Character Limit</Typography>
                <TextField
                  size="small"
                  value={characterLimit}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '');
                    if (v === '' || (parseInt(v) > 0 && parseInt(v) <= 9999)) setCharacterLimit(v);
                  }}
                  placeholder="1000"
                  sx={{ width: 150, ...textFieldStyles }}
                  helperText="Max characters per copy"
                />
              </Box>
            )}

            {/* DTC Framework info banner */}
            {pipelineTab === 1 && (
              <Box sx={{ mb: 3, p: 2, borderRadius: 1, bgcolor: isDarkMode ? 'rgba(76, 175, 80, 0.08)' : 'rgba(76, 175, 80, 0.05)', border: `1px solid ${isDarkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)'}` }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: '#4caf50' }}>DTC Framework Mode</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Generates structured output: Title, 3-Sentence Description (S1/S2/S3), 5 Bullets (header + body), Care Instructions, and Specifications. Voice is auto-assigned (Craft for hand-made, Design for machine-made).
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* File Upload Zone */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Excel / CSV File
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Required column: ItemID. Optional: AdditionalQuery for custom instructions per item.
            </Typography>

            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileSelect} style={{ display: 'none' }} />

            <Box
              onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${isDragging ? primaryColor : file ? '#4CAF50' : theme.palette.divider}`,
                borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer',
                bgcolor: isDragging
                  ? TOOL_COLORS.OTHER.light
                  : file
                    ? (isDarkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                    : 'transparent',
                transition: 'all 0.3s ease', mb: 2,
                '&:hover': {
                  borderColor: file ? '#4CAF50' : primaryColor,
                  bgcolor: file
                    ? (isDarkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                    : TOOL_COLORS.OTHER.light,
                },
              }}
            >
              {!file ? (
                <>
                  <CloudUploadIcon sx={{ fontSize: 48, color: isDragging ? primaryColor : 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" gutterBottom>
                    {isDragging ? 'Drop file here' : 'Drop your Excel or CSV file here or click to browse'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Excel (.xlsx, .xls) or CSV (.csv) files supported
                  </Typography>
                </>
              ) : (
                <>
                  <CheckCircleIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                  <Typography variant="body1" gutterBottom>1 file selected</Typography>
                  <Typography variant="caption" color="text.secondary">Click or drop to replace</Typography>
                </>
              )}
            </Box>

            {/* File Info Bar */}
            {file && (
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                p: 1, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1, transition: 'all 0.2s ease', mb: 1,
                '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)', borderColor: primaryColor },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                  <DescriptionIcon sx={{ color: primaryColor, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                    ({(file.size / 1024).toFixed(1)} KB)
                  </Typography>
                </Box>
                <Button size="small" onClick={(e) => { e.stopPropagation(); clearFile(); }}
                  sx={{ color: theme.palette.error.main, minWidth: 'auto', p: 0.5 }}>
                  <CloseIcon fontSize="small" />
                </Button>
              </Box>
            )}

            {/* Action Row */}
            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mt: 3, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={processing ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                onClick={startProcessing}
                disabled={processing || !file}
                sx={{
                  flex: { xs: '1 1 100%', sm: 1 },
                  minWidth: { xs: '100%', sm: 200 },
                  py: 1.2, backgroundColor: primaryColor, color: 'white', textTransform: 'none', fontWeight: 600,
                  '&:hover': { backgroundColor: primaryHover },
                  '&:disabled': { backgroundColor: isDarkMode ? alpha('#fff', 0.12) : theme.palette.grey[300], color: isDarkMode ? alpha('#fff', 0.3) : theme.palette.grey[500] },
                }}
              >
                {processing ? 'Processing...' : (pipelineTab === 0 ? 'PROCESS' : 'GENERATE DTC COPY')}
              </Button>

              <Button
                variant="outlined"
                startIcon={<FaDownload />}
                onClick={downloadTemplate}
                sx={{
                  flex: { xs: '1 1 100%', sm: '0 0 auto' },
                  py: 1.2, textTransform: 'none', fontWeight: 600,
                  borderColor: primaryColor, color: primaryColor,
                  '&:hover': { borderColor: primaryHover, bgcolor: TOOL_COLORS.OTHER.light },
                }}
              >
                DOWNLOAD TEMPLATE
              </Button>

            </Box>
          </Paper>

          {/* Processing Status */}
          {status && (
            <Paper sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: primaryColor }}>Processing Status</Typography>
                <Chip
                  label={statusVal === 'completed' ? 'Completed' : statusVal === 'error' ? 'Error' : 'Processing...'}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: statusVal === 'completed' ? alpha('#4caf50', 0.15) : statusVal === 'error' ? alpha('#f44336', 0.15) : alpha(primaryColor, 0.15),
                    color: statusVal === 'completed' ? '#4caf50' : statusVal === 'error' ? '#f44336' : primaryColor,
                  }}
                />
              </Box>

              <LinearProgress
                variant="determinate"
                value={progressPct}
                sx={{ mb: 2, height: 8, borderRadius: 4, backgroundColor: alpha(primaryColor, 0.15), '& .MuiLinearProgress-bar': { backgroundColor: primaryColor, borderRadius: 4 } }}
              />

              <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Progress: <strong>{processedItems}/{totalItems}</strong> items ({Math.round(progressPct)}%)
                </Typography>
                {failedItems > 0 && (
                  <Typography variant="body2" sx={{ color: '#f44336' }}>Failed: <strong>{failedItems}</strong></Typography>
                )}
              </Box>

              {/* Results auto-download and open in S3 on completion */}
            </Paper>
          )}
        </Grid>

        {/* Right Column — Status & Instructions */}
        <Grid item xs={12} md={4}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

          {/* Instructions — varies by pipeline tab */}
          <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: primaryColor }}>Instructions</Typography>
            {pipelineTab === 0 ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>1. Select product category (Rugs or Non-Rugs)</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>2. Choose generation mode:</Typography>
                <Typography variant="body2" sx={{ mb: 0.5, pl: 2 }}><strong>Generate New</strong> — AI creates fresh copy using product specs + image</Typography>
                <Typography variant="body2" sx={{ mb: 1, pl: 2 }}><strong>Use Existing</strong> — pulls existing copy from Items Detail database</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>3. Set character limit (default 1000)</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>4. Download the template and fill in ItemIDs</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>5. Upload your file and click Process</Typography>
                <Typography variant="body2">6. Results auto-download as Excel when complete</Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>1. Select product category (Rugs or Non-Rugs)</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>2. Choose generation mode:</Typography>
                <Typography variant="body2" sx={{ mb: 0.5, pl: 2 }}><strong>Generate New</strong> — AI generates structured DTC copy with voice assignment</Typography>
                <Typography variant="body2" sx={{ mb: 1, pl: 2 }}><strong>Use Existing</strong> — pulls existing copy from Items Detail database</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>3. Download the template and fill in ItemIDs</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>4. Upload your file and click Process</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>5. Voice is auto-assigned: <strong>Craft</strong> (hand-made) or <strong>Design</strong> (machine-made)</Typography>
                <Typography variant="body2">6. Results auto-download as Excel with structured columns</Typography>
              </>
            )}
          </Paper>

          <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: primaryColor }}>How It Works</Typography>
            {pipelineTab === 0 ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  The generator looks up each ItemID in the Items Detail database to pull product specifications (size, color, material, weave, origin, style, etc.).
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  It also matches each item to product images from the image links database. When an image is found, the AI analyzes it for visual details.
                </Typography>
                <Typography variant="body2">
                  Powered by <strong>Google Gemini</strong> — optimized prompts for Safavieh product marketing that drive conversions.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Looks up each ItemID in Items Detail for full product data: Collection, Design, DesignName, Construction, Material, Origin, Style, Pattern, Dimensions, and more.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Auto-assigns <strong>Craft voice</strong> (hand-knotted, hand-tufted, hand-woven, Couture, European origin) or <strong>Design voice</strong> (everything else) based on product attributes.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Generates structured copy following the Safavieh DTC Copy Framework: Title (formula-based), 3-Sentence Description, 5 Bullets with headers, Care instructions, and Specifications.
                </Typography>
                <Typography variant="body2">
                  Powered by <strong>Claude Opus</strong> with product images when available.
                </Typography>
              </>
            )}
          </Paper>

          {/* AI Prompts — only show for Classic mode */}
          {pipelineTab === 0 && (
            <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: primaryColor }}>AI Prompts</Typography>

              <Accordion sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${theme.palette.divider}`, mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Rug Prompt</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.7, bgcolor: isDarkMode ? '#1a1a1a' : '#f5f5f5', borderRadius: 1, p: 2, whiteSpace: 'pre-wrap' }}>
{`You are an expert e-commerce copywriter for Safavieh, one of the largest home furnishing brands in the world. Write a compelling, conversion-optimized marketing description for this area rug.

Product Specifications:
- Size: [size]
- Color: [color]
- Shape: [shape] [height]
- Construction: [weave]
- Material: [content]
- Made in: [origin]
- Style: [style]
- Pattern: [pattern]
- Machine Washable: Yes (if applicable)
- Non-Slip Backing: Yes (if applicable)
- OEKO-TEX Certified: Yes (if applicable)

Writing Guidelines:
1. Open with an evocative, lifestyle-driven sentence that helps the customer visualize the rug in their home
2. Highlight the key selling points: material quality, construction technique, and practical benefits
3. Use sensory language (soft underfoot, rich texture, vibrant hues) to create desire
4. Mention durability and ease of maintenance where applicable
5. Close with a statement about how it ties the room together or elevates the space
6. Write in a warm, confident, aspirational tone — not salesy or pushy
7. Aim for 3-4 flowing paragraphs`}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${theme.palette.divider}` }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Non-Rug (Furniture/Lighting) Prompt</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.7, bgcolor: isDarkMode ? '#1a1a1a' : '#f5f5f5', borderRadius: 1, p: 2, whiteSpace: 'pre-wrap' }}>
{`You are an expert e-commerce copywriter for Safavieh, one of the largest home furnishing brands in the world. Write a compelling, conversion-optimized marketing description for this home furnishing product.

Product Details:
- Description: [description]
- Color: [color]
- Material: [content]

Writing Guidelines:
1. Open with a lifestyle-driven sentence that helps the customer picture this piece in their home
2. Highlight the craftsmanship, materials, and design aesthetic
3. Mention practical benefits (easy assembly, versatile placement, durability)
4. Use sensory and visual language to create an emotional connection
5. Close with how this piece complements or transforms a living space
6. Write in a warm, confident, aspirational tone
7. Aim for 2-3 flowing paragraphs`}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                When a product image is found, the AI also analyzes visual details (colors, textures, patterns) from the image.
              </Typography>
            </Paper>
          )}

          {/* DTC Framework Details — only show for DTC mode */}
          {pipelineTab === 1 && (
            <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: primaryColor }}>DTC Framework Output</Typography>

              <Accordion sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${theme.palette.divider}`, mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Voice Assignment Rules</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.7, bgcolor: isDarkMode ? '#1a1a1a' : '#f5f5f5', borderRadius: 1, p: 2, whiteSpace: 'pre-wrap' }}>
{`CRAFT Voice (hand-made, artisan):
  Rugs: Weave contains "hand" (hand-knotted, hand-tufted, hand-woven, hand-hooked)
  Furniture: Couture collection, RLR/LRL prefix, "hand" in description, or European origin

  - Origin included in description
  - Construction technique highlighted
  - Handmade variation language in B1

DESIGN Voice (everything else):
  - No origin in description (Specs only)
  - Focus on visual effect, practical performance, room impact
  - B1 about material performance`}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${theme.palette.divider}`, mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Output Structure</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.7, bgcolor: isDarkMode ? '#1a1a1a' : '#f5f5f5', borderRadius: 1, p: 2, whiteSpace: 'pre-wrap' }}>
{`Title:       Formula-based ({Collection} {Descriptors} {Construction} {Material} {Qualifier} Rug)
Description: 3 sentences (S1: what it is, S2: what makes it different, S3: physical/practical)
B1-B5:       5 bullets, each with Header (2-4 words) + Body (25-45 words)
Care 1-7:    Material-specific care instructions
Specs:       Construction, Content, Pile Height, Backing, Origin, Style, Pattern, Indoor/Outdoor

Voice Rules:
- Product is the subject (no "you" or "your" in descriptions)
- Gain-frame everything (what it gives, not what it prevents)
- No marketing filler ("elevate," "timeless elegance," etc.)
- No color references (copy shared across colorways)
- No em dashes or en dashes`}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${theme.palette.divider}` }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Data Columns Used</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.7, bgcolor: isDarkMode ? '#1a1a1a' : '#f5f5f5', borderRadius: 1, p: 2, whiteSpace: 'pre-wrap' }}>
{`From Items Detail database:
  ItemID, Collection, DESIGN, DesignName, ProductType,
  Description, Color, Size, Shape, RugHeight,
  Weave, Content, Origin, Style, Pattern,
  Dimensions (WxDxH), Indoor/Outdoor,
  MachineWashable, NonSlip, OEKO-TEX

From Image Links database:
  Best matching product image per ItemID`}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                The DTC Framework follows the Safavieh DTC Copy Framework (DTC_RUGS.md / DTC_FURNITURE.md) used in the production copy pipeline.
              </Typography>
            </Paper>
          )}

          <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: primaryColor }}>Template Format</Typography>
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', bgcolor: isDarkMode ? '#1a1a1a' : '#f0f0f0', borderRadius: 1, p: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 4, fontWeight: 700 }}>
                <span>ItemID</span><span>AdditionalQuery</span>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', gap: 4, color: 'text.secondary' }}>
                <span>CLS123-5</span><span>(optional notes)</span>
              </Box>
              <Box sx={{ display: 'flex', gap: 4, color: 'text.secondary' }}>
                <span>SGH456-8</span><span>emphasize outdoor use</span>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#f44336' }}>Banned Words</Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.6, display: 'block' }}>
              {pipelineTab === 0
                ? 'exceptional, finest, first rate, forever, guarantee, high grade, high quality, long lasting, longevity, perfection, plush, premium, promises, resilience, top-notch, top grade, very good, year after year, years, high-end'
                : 'elevate, timeless elegance, luxurious, sumptuous, curated, sophisticated, transform your space, boasts, oasis, exceptional, finest, first rate, forever, guarantee, high grade, high quality, long lasting, longevity, perfection, plush, premium, promises, resilience, top-notch, top grade, very good, year after year, years, high-end'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MarketingCopies;
